import type { UIEvent } from 'react'
import { AudioLines, Hand } from 'lucide-react'
import { Input } from 'components/ds/Input'
import { useAppSelector } from 'hooks'
import { formatAge } from 'utils/format-relative-time'
import { sessionOwnerLabel } from '../sessionLabel'
import { ExportMenu } from './ExportMenu'

interface SessionListProps {
    search: string
    status: 'all' | 'active'
    onSearchChange: (v: string) => void
    onStatusChange: (v: 'all' | 'active') => void
    onSelect: (tabId: string) => void
    onLoadMore: () => void
}

// How close to the bottom (px) the operator has to scroll before the next page is fetched — far
// enough ahead that the rows are usually there by the time the end of the list is reached.
const LOAD_MORE_THRESHOLD_PX = 240

export function SessionList({ search, status, onSearchChange, onStatusChange, onSelect, onLoadMore }: SessionListProps) {
    const order = useAppSelector((s) => s.dispatchHelp.sessionOrder)
    const sessions = useAppSelector((s) => s.dispatchHelp.sessions)
    const unreadByTab = useAppSelector((s) => s.dispatchHelp.unreadByTab)
    const activeSessionId = useAppSelector((s) => s.dispatchHelp.activeSessionId)
    const loading = useAppSelector((s) => s.dispatchHelp.loadingSessions)
    const loadingMore = useAppSelector((s) => s.dispatchHelp.loadingMoreSessions)
    const hasMore = useAppSelector((s) => s.dispatchHelp.hasMoreSessions)

    // The server pages by keyset cursor (30 at a time), so the list is only ever the window fetched so
    // far — pull the next one as the operator nears the end. `onLoadMore` no-ops while a page is in
    // flight, so repeated scroll events can't stack requests.
    function handleScroll(e: UIEvent<HTMLDivElement>) {
        if (!hasMore || loadingMore) return
        const el = e.currentTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) onLoadMore()
    }

    return (
        <div className="flex h-full flex-col border-r border-slate-200 dark:border-slate-700">
            <div className="space-y-2 border-b border-slate-200 p-3 dark:border-slate-700">
                <Input placeholder="Search title, email, or phone…" value={search} onChange={(e) => onSearchChange(e.target.value)} />
                <div className="flex items-center gap-1">
                    {(['all', 'active'] as const).map((s) => (
                        <button
                            key={s}
                            onClick={() => onStatusChange(s)}
                            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${
                                status === s
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                    {/* Bulk session download (JSONL/CSV) for product review. */}
                    <ExportMenu />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
                {loading && order.length === 0 && <div className="p-4 text-center text-xs text-slate-400">Loading…</div>}
                {!loading && order.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No sessions</div>}
                {order.map((tabId) => {
                    const session = sessions[tabId]
                    if (!session) return null
                    const unread = unreadByTab[tabId] ?? 0
                    const active = tabId === activeSessionId
                    return (
                        <button
                            key={tabId}
                            onClick={() => onSelect(tabId)}
                            className={`flex w-full flex-col gap-0.5 border-b border-slate-100 px-3 py-2.5 text-left transition-colors dark:border-slate-800 ${
                                active ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{session.title || 'Conversation'}</span>
                                <span className="shrink-0 text-[10px] text-slate-400">{formatAge(session.lastActivityAt)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-xs text-slate-500 dark:text-slate-400">{sessionOwnerLabel(session)}</span>
                                <div className="flex shrink-0 items-center gap-1.5">
                                    {/* Session has at least one voice call — lets an operator spot voice usage from the list. */}
                                    {session.hasVoice && (
                                        <span title="Has voice call">
                                            <AudioLines className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                                        </span>
                                    )}
                                    {/* Driver raised a hand and no one has taken over yet. */}
                                    {session.helpRequested && !session.takeover && (
                                        <span title="Driver requested a dispatcher">
                                            <Hand className="h-3.5 w-3.5 animate-pulse text-amber-500" />
                                        </span>
                                    )}
                                    {session.takeover && (
                                        <span className="h-2 w-2 rounded-full bg-emerald-500" title={`${session.takeover.dispatcherName} joined`} />
                                    )}
                                    {unread > 0 && <span className="rounded-full bg-blue-500 px-1.5 text-[10px] font-semibold text-white">{unread}</span>}
                                </div>
                            </div>
                            {/* Cross-tenant: which company this driver belongs to. */}
                            {session.projectName && <span className="truncate text-[10px] text-slate-400 dark:text-slate-500">{session.projectName}</span>}
                        </button>
                    )
                })}
                {/* Footer doubles as the manual fallback: on a very tall viewport the first page may not
                    overflow, so no scroll event ever fires — the operator can click instead. */}
                {loadingMore && <div className="p-3 text-center text-xs text-slate-400">Loading more…</div>}
                {!loadingMore && hasMore && (
                    <button onClick={onLoadMore} className="w-full p-3 text-center text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">
                        Load older conversations
                    </button>
                )}
            </div>
        </div>
    )
}
